/**
 * AIAnalysisBox：AI 分析内容展示容器。
 * - 负责根据流式会话状态切换空态、加载态、错误态与正常展示态。
 * - 正常态使用 aigc 的 MainContentCmp 渲染 Markdown 回答。
 */
import React, {
  memo,
  useCallback,
  useMemo,
} from 'react';
import { LoadingTip } from 'tea-component';
import { useIntl } from 'umi';
import type { IUseStreamingConversationResult } from '@dbl/core/global';
import { MainContentCmp } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp';

import type { IAiAnalysisStreamingInput } from '../types';
import Empty from './AiStatus/Empty';
import Error from './AiStatus/Error';
import Loading from './AiStatus/Loading';
import NoChart from './AiStatus/NoChart';
import './index.scss';

export interface IAIAnalysisBoxProps {
  session: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  /** false 表示当前分析范围内无可用图表（如无 chart 卡片或未选图表） */
  hasAnalyzableCharts?: boolean;
  onRunAnalysis?: (options?: { refresh?: boolean; }) => Promise<void>;
  onOpenAnalysisApproach?: () => void;
  onStopClick?: (fallbackStop: () => void) => void;
}

const AIAnalysisBox: React.FC<IAIAnalysisBoxProps> = (props) => {
  const {
    session,
    hasAnalyzableCharts = true,
    onRunAnalysis,
    onOpenAnalysisApproach,
    onStopClick,
  } = props;
  const { formatMessage } = useIntl();

  const {
    conversations,
    streamingState,
    cancelStreaming,
    clearConversations,
  } = session;

  const isSending = streamingState === 'pending' || streamingState === 'progressing';
  const hasConversation = conversations.length > 0;

  // ===== 派生数据：从 conversations 提取最新回答文本与错误信息 =====
  const {
    latestConversation,
    latestAnswerText,
    latestErrorMessage,
  } = useMemo(() => {
    const latest = conversations[conversations.length - 1];
    const answerList = latest?.answer || [];
    const errorBlock = answerList.find((item) => item.type === 'error');
    // 从后向前遍历，取最近一条 text 类型的回答块（兼容多轮对话中历史记录残留）。
    let latestText = '';
    for (let i = answerList.length - 1; i >= 0; i -= 1) {
      const item = answerList[i];
      if (item?.type === 'text' && typeof item.value === 'string') {
        latestText = item.value;
        break;
      }
    }
    return {
      latestConversation: latest,
      latestAnswerText: latestText,
      latestErrorMessage: typeof errorBlock?.value === 'string' ? errorBlock?.value : '',
    };
  }, [conversations]);

  const handleSend = useCallback(async () => {
    if (isSending || !onRunAnalysis) {
      return;
    }
    try {
      await onRunAnalysis();
    } catch (_error) {
      // 错误状态由通用 hook 内部统一托管并写入 answer，本层无需额外处理。
    }
  }, [
    isSending,
    onRunAnalysis,
  ]);

  /**
   * 停止交互约定：
   * - 若上层提供了 onStopClick，则把 fallbackStop 交给上层做“取消 + 清理”或额外逻辑；
   * - 否则直接执行 fallbackStop，避免会话继续占用。
   */
  const handleStop = useCallback(() => {
    const fallbackStop = () => {
      cancelStreaming();
      clearConversations();
    };
    if (onStopClick) {
      onStopClick(fallbackStop);
      return;
    }
    fallbackStop();
  }, [cancelStreaming, clearConversations, onStopClick]);

  // 状态优先级：无图表 > loading（无内容） > 空态 > error（无内容） > 正常展示
  const showLoadingStatus = isSending && !latestAnswerText;
  const showErrorStatus = streamingState === 'rejected' && !latestAnswerText;

  if (!hasAnalyzableCharts) {
    return <NoChart />;
  }

  if (showLoadingStatus) {
    return (
      <Loading onStopClick={handleStop} />
    );
  }

  if (!hasConversation) {
    return (
      <Empty onOpenAnalysisApproach={onOpenAnalysisApproach} />
    );
  }

  if (showErrorStatus) {
    return (
      <Error
        onRefresh={handleSend}
        errorMessage={latestErrorMessage}
      />
    );
  }

  return (
    <div className="dbl-ai-analysis-box dbl-hover-scrollbar">
      {latestAnswerText ? (
        <>
          <MainContentCmp
            value={latestAnswerText}
            answer={latestConversation?.answer}
            from="ds-agent"
            messageId={latestConversation?.messageId}
            isShowReference={false}
          />
          {isSending ? (
            <div className="dbl-ai-analysis-box__streaming-action">
              <LoadingTip loadingText=''/>
              {/* <Button type="link" onClick={handleStop}>
                {formatMessage({ id: 'dbl_ai_analysis_stream_stop' })}
              </Button> */}
            </div>
          ) : null}
        </>
      ) : (
        formatMessage({ id: 'dbl_ai_analysis_stream_empty' })
      )}
    </div>
  );
};

export default memo(AIAnalysisBox);

